<script setup lang="ts">
import Edit from '~/assets/icons/edit.svg'
import AngleDown from '~/assets/icons/angle-down.svg'
import CheckCircle from '~/assets/icons/check-circle.svg'
import BaseButton from '~/components/common/BaseButton.vue'
import BasePhoneInput from '~/components/common/BasePhoneInput.vue'
import { Form as VeeForm } from 'vee-validate'
import type { TGeneral } from '~/types/app.type'
import { twMerge } from 'tailwind-merge'
import { onClickOutside } from '@vueuse/core'

type TProps = {
  name: string
  modelValue?: string
  label?: string
  canEdit?: boolean
  updated?: boolean
  isEditable?: boolean
  isSubmitting?: boolean
  labelClasses?: string
  containerClasses?: string
  type?: 'text' | 'email' | 'phone'
  rules?: string
  originalValue?: string
}

const emit = defineEmits([
  'update:modelValue',
  'updateField',
  'onSubmit',
  'cancelEdit',
])
const props = defineProps<TProps>()

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const internalValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (newVal: any) => {
    internalValue.value = newVal
  },
  { immediate: true },
)

const hasChanges = computed(() => {
  const originalValue =
    props.originalValue !== undefined
      ? props.originalValue
      : internalValue.value

  if (!originalValue && value.value) return true
  if (originalValue) return value.value !== originalValue
  return false
})

const inputRules = computed(() => {
  if (props.type === 'text') return ''
  if (!props.rules) return 'email'
  return props.rules
})

const onSubmit = (values: TGeneral) => {
  emit('onSubmit', {
    key: props.name,
    values: {
      value: values[props.name],
      updated: true,
    },
  })
}

const formRef = ref(null)

const showEditField = () => {
  emit('updateField', {
    key: props.name,
    values: { isEditable: true },
  })
}

const cancelEdit = () => {
  if (props.isEditable && !props.isSubmitting && !props.updated) {
    const originalValue =
      props.originalValue !== undefined
        ? props.originalValue
        : internalValue.value

    emit('update:modelValue', originalValue)

    emit('updateField', {
      key: props.name,
      values: { isEditable: false },
    })

    emit('cancelEdit')
  }
}

onClickOutside(formRef, cancelEdit)
</script>

<template>
  <div
    :class="[twMerge('flex gap-y-3.5 w-full flex-col', props.containerClasses)]"
  >
    <span v-if="label" class="font-semibold">{{ label }}</span>
    <div
      v-if="!isEditable"
      :class="[
        twMerge(
          'flex items-center gap-x-2 justify-between pl-7 pr-2 w-full',
          props.labelClasses,
        ),
      ]"
    >
      <p class="flex-shrink overflow-hidden text-ellipsis" :title="value">
        {{ value }}
      </p>
      <button
        v-if="canEdit"
        class="ml-auto hover:text-p-500 transition-colors"
        @click="showEditField()"
      >
        <Edit />
      </button>
    </div>

    <template v-if="canEdit">
      <VeeForm v-slot="{ handleSubmit, meta }" as="">
        <form
          v-show="isEditable && !updated"
          ref="formRef"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <BasePhoneInput
            v-if="type === 'phone'"
            v-model="value"
            :name="name"
            :rules="rules"
            :is-editable="isEditable"
            :updated="updated"
            :original-value="
              originalValue !== undefined ? originalValue : internalValue
            "
            :dropdown-options="{
              showCountryName: true,
              showDialCodeInSelection: false,
              showFlags: false,
            }"
            :input-options="{
              showDialCode: false,
            }"
            without-label
            class="flex-grow"
            border-classes-input="!border-none !gap-x-1"
          >
            <template #icon-right>
              <BaseButton
                type="submit"
                class="bg-p-600 h-full border border-p-500 !p-3 text-sm font-semibold !rounded disabled:!bg-grey-300 disabled:!border-grey-300 disabled:!text-grey-400 disabled:cursor-not-allowed"
                :class="[
                  !meta.valid || !value || isSubmitting || !hasChanges
                    ? '!border-grey-400'
                    : 'border-p-500',
                ]"
                :loading="isSubmitting"
                :disabled="!meta.valid || !value || isSubmitting || !hasChanges"
              >
                Save
              </BaseButton>
            </template>
            <template #arrow-icon>
              <AngleDown class="size-4" />
            </template>
          </BasePhoneInput>
          <BaseInput
            v-else
            v-model="value"
            :name="name"
            :type="type"
            :rules="inputRules"
            class="flex-grow"
            border-classes-input="!border-none !gap-x-1"
            input-classes="border border-p-500 h-[42px] rounded"
          >
            <template #icon-right>
              <BaseButton
                type="submit"
                class="bg-p-600 h-full border !p-3 text-sm font-semibold !rounded disabled:!bg-grey-300 disabled:!border-grey-300 disabled:!text-grey-400 disabled:cursor-not-allowed"
                :class="[
                  !meta.valid || !value || isSubmitting || !hasChanges
                    ? '!border-grey-400'
                    : 'border-p-500',
                ]"
                :loading="isSubmitting"
                :disabled="!meta.valid || !value || isSubmitting || !hasChanges"
              >
                Save
              </BaseButton>
            </template>
          </BaseInput>
        </form>
      </VeeForm>

      <div
        v-if="updated"
        class="flex justify-between h-[42px] items-center gap-x-1"
      >
        <span
          class="pl-3 rounded border border-good h-full w-full flex items-center justify-start"
        >
          {{ value }}
        </span>
        <div
          class="border rounded border-good bg-good h-full flex items-center justify-center px-5"
        >
          <CheckCircle class="size-5" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
:deep(.vue-tel-input) {
  @apply border-none !important;
}

:deep(.vue-tel-input:focus-within) {
  @apply border-none ring-0 ring-offset-0 !important;
}

:deep(.vue-tel-input .vti__dropdown) {
  @apply py-2.5 px-3 bg-s-200 rounded font-semibold text-sm w-full max-w-[30%];

  color: #23252c;
}

:deep(.vue-tel-input .vti__dropdown .vti__selection) {
  @apply flex gap-x-1.5 items-center justify-between text-sm;
}

:deep(.vue-tel-input .vti__phone) {
  border: 1px solid !important;

  @apply py-2 px-3 rounded border border-p-500 !important;
}
</style>
